// src/features/system/developer/components/simulations/table-ops/api/simulationApi.ts
// ────────────────────────────────────────────────────────────────────────────
// FE client for the TableOps Traffic Simulation engine.
// Endpoint paths match server/bff/modules/services/table-ops/simulation/routes.mjs.
// ────────────────────────────────────────────────────────────────────────────

import { bff } from '@/lib/http/bff';

const BASE = '/table-ops/simulation';

/* ─── Types ─────────────────────────────────────────────────────────── */

/** Per-table guest detail for floor-plan sync */
export interface TableGuestDetail {
  guestId: string;
  covers: number;
  stage: string;
  sessionKey: string | null;
  round: number;
  totalRounds: number;
  subtotal: number;
  itemCount: number;
  wantsDessert: boolean;
  isReservation: boolean;
  paymentMethod: string | null;
  merged?: boolean;
}

export interface SimulationState {
  running: boolean;
  paused: boolean;
  runId?: string;
  startedAt?: string;
  simTime: string;
  simMinute: number;
  dayNumber: number;
  uptime_ms: number;
  timezone?: string;
  occupancy: number;
  guests: number;
  tables: {
    total: number;
    occupied: number;
    free: number;
    /** Table keys currently occupied by sim guests */
    occupiedKeys?: string[];
    /** Per-table guest detail (stage, covers, session, round, etc.) */
    tableGuests?: Record<string, TableGuestDetail>;
  };
  stats: SimStats;
  profile: string;
  profileConfig?: Partial<ProfileConfig>;
  shifts: ShiftState;
  bots?: BotStatus[];
  tenantId?: number;
  tenantName?: string;
}

/** Finished-run summary (from history) */
export interface RunRecord {
  run_id: string;
  category: string;
  profile: string;
  started_at: string;
  stopped_at: string;
  duration_ms: number;
  day: number;
  sim_minute: number;
  stats: SimStats;
}

export interface SimStats {
  total_guests: number;
  total_tables_opened: number;
  sessions_opened: number;
  sessions_closed: number;
  orders_placed: number;
  total_items: number;
  total_revenue: number;
  total_tips: number;
  payment_methods: Record<string, number>;
  shifts_opened: number;
  shifts_closed: number;
  no_shows: number;
  walk_aways: number;
  desserts_ordered: number;
  reservations: number;
  walk_ins: number;
}

export interface ShiftState {
  day: number;
  schedule: ShiftScheduleEntry[];
  activeShifts: ActiveShift[];
  totalOpened: number;
  totalClosed: number;
}

export interface ShiftScheduleEntry {
  label: string;
  nominalOpen: string;
  nominalClose: string;
  actualOpen: string;
  actualClose: string;
  registers: number;
}

export interface ActiveShift {
  id: string;
  label: string;
  registerNumber: number;
  openedAt: string;
  closedAt: string | null;
  openingFloat: number;
  status: 'open' | 'closed';
}

export interface ProfilePreset {
  key: string;
  name: string;
  description: string;
  time_compression: number;
}

/**
 * Full profile configuration — all tunable parametrizations.
 * Used by the admin panel for live tweaks.
 */
export interface ProfileConfig {
  // Timing
  tick_interval_ms: number;
  time_compression: number;
  start_at_wall_clock: boolean;
  timezone?: string;

  // Arrivals
  peak_arrival_boost: number;
  no_show_probability: number;
  walk_away_probability: number;
  reservation_ratio: number;

  // Guest profile
  covers_distribution: Record<string, number>;
  multi_check_probability: number;

  // Ordering
  drink_first_probability: number;
  dessert_probability: number;
  order_rounds: Record<string, number>;
  items_per_person: { min: number; max: number };
  hot_item_weight: number;

  // Dining timing
  meal_duration_minutes: { min: number; max: number };
  lingering_minutes: { min: number; max: number };
  browsing_minutes: { min: number; max: number };
  kitchen_speed_factor: number;
  service_speed_factor: number;

  // Payments
  payment_methods: Record<string, number>;
  tip_distribution: Record<string, number>;

  // Shifts
  shifts: {
    template: Array<{
      label: string;
      nominalOpen: string;
      nominalClose: string;
      registerCount: number;
    }>;
    jitter_minutes: number;
    opening_float: number;
  };

  // Meta
  occupancy: Record<string, number>;
  hot_items: string[];
  name: string;
  description: string;
}

export interface SimEvent {
  event: string;
  simTime: string;
  simMinute: number;
  day: number;
  ts: string;
  timezone?: string;
  actor?: string;
  [key: string]: unknown;
}

/* ─── Bot identity types ────────────────────────────────────────────── */

export interface BotStatus {
  key: string;
  displayName: string;
  description: string;
  username: string;
  idUser: number;
  status: 'active';         // ephemeral bots are always active
  tenantName: string;
  roles: string[];
  permissions: string[];
  error: string | null;
}

/* ─── API calls ─────────────────────────────────────────────────────── */

export const startSimulation = (opts?: {
  profile?: string;
  overrides?: Record<string, unknown>;
  tenantId?: number;
  tenantName?: string;
}): Promise<{ ok: boolean; message: string; state: SimulationState }> =>
  bff.post(`${BASE}/start`, opts || {});

export const stopSimulation = (): Promise<{ ok: boolean; state: SimulationState }> =>
  bff.post(`${BASE}/stop`, {});

export const pauseSimulation = (): Promise<{ ok: boolean; state: SimulationState }> =>
  bff.post(`${BASE}/pause`, {});

export const resumeSimulation = (): Promise<{ ok: boolean; state: SimulationState }> =>
  bff.post(`${BASE}/resume`, {});

export const updateSimProfile = (
  overrides: Record<string, unknown>,
): Promise<{ ok: boolean; profile: Record<string, unknown> }> =>
  bff.patch(`${BASE}/profile`, overrides);


export const getSimBots = (): Promise<{ ok: boolean; tenantName: string; tenantId: number; bots: BotStatus[] }> =>
  bff.get(`${BASE}/bots`);
export const getSimState = (): Promise<SimulationState> =>
  bff.get(`${BASE}/state`);

export const getSimProfiles = (): Promise<{ profiles: ProfilePreset[] }> =>
  bff.get(`${BASE}/profiles`);

export const getSimShifts = (): Promise<{
  ok: boolean;
  shifts: ShiftState;
  schedule: ShiftScheduleEntry[];
  history: ActiveShift[];
}> => bff.get(`${BASE}/shifts`);

export const getSimRuns = (): Promise<{ runs: RunRecord[] }> =>
  bff.get(`${BASE}/runs`);

/** Fetch recent activity events from the engine ring buffer (survives SPA refresh). */
export const getSimEvents = (limit = 200): Promise<{ events: SimEvent[] }> =>
  bff.get(`${BASE}/events?limit=${limit}`);

/** Manual trigger — fire a single stimulus */
export type TriggerAction = 'arrival' | 'order' | 'payment' | 'shift_open' | 'shift_close' | 'tick';
export const triggerSimAction = (
  action: TriggerAction,
): Promise<{ ok: boolean; action: string; [key: string]: unknown }> =>
  bff.post(`${BASE}/trigger`, { action });

/* ─── SSE stream helper ─────────────────────────────────────────────── */

/**
 * Connect to the simulation SSE stream.
 * Returns a cleanup function.
 */
export function connectSimStream(
  onEvent: (evt: SimEvent) => void,
  onError?: (err: Event) => void,
  _opts?: { tenantId?: number | string; tenantName?: string },
): () => void {
  const url = `/bff${BASE}/stream`;
  const source = new EventSource(url);

  source.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data) as SimEvent;
      onEvent(data);
    } catch { /* ignore parse errors */ }
  };

  source.onerror = (e) => {
    onError?.(e);
  };

  return () => source.close();
}
